import React from 'react';
import { Phone, ExternalLink, Building2, CalendarDays, CalendarClock, StickyNote, Tag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import RatingPicker from './RatingPicker';
import {
  fullName, initials, formatBusinessDate, socialPlatformLabel, socialProfileUrl, todayISODate,
  businessWhatsAppUrl, openBusinessWhatsApp, contactStatusLabel, contactStatusBadgeClass,
} from '../../lib/businessHelpers';

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-xs text-zinc-400">{label}</p>
      <div className="text-sm text-zinc-800">{children}</div>
    </div>
  </div>
);

const ContactDetailDialog = ({ open, onOpenChange, contact }) => {
  if (!contact) return null;
  const profileUrl = socialProfileUrl(contact.social_platform, contact.social_handle);
  const reminderDates = contact.reminder_dates || [];
  const today = todayISODate();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-zinc-200 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center font-semibold text-lg flex-shrink-0">
              {initials(contact)}
            </div>
            <DialogTitle className="text-left">{fullName(contact)}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RatingPicker value={contact.rating} variant="fuochi" size={18} />

          {contact.telefono && (
            <Row icon={Phone} label="Telefono">
              <a
                href={businessWhatsAppUrl(contact.telefono)}
                onClick={(e) => { e.preventDefault(); openBusinessWhatsApp(contact.telefono); }}
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-700 hover:underline"
              >
                {contact.telefono}
              </a>
            </Row>
          )}

          {contact.social_platform && (
            <Row icon={ExternalLink} label="Profilo social">
              {profileUrl ? (
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline inline-flex items-center gap-1">
                  {socialPlatformLabel(contact.social_platform)} {contact.social_handle}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span>{socialPlatformLabel(contact.social_platform)} {contact.social_handle}</span>
              )}
            </Row>
          )}

          {contact.azienda_attuale && (
            <Row icon={Building2} label="Azienda attuale">{contact.azienda_attuale}</Row>
          )}

          {contactStatusLabel(contact.status) && (
            <Row icon={Tag} label="Status">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${contactStatusBadgeClass(contact.status)}`}>
                {contactStatusLabel(contact.status)}
              </span>
            </Row>
          )}

          {contact.data_contatto && (
            <Row icon={CalendarDays} label="Data contatto">
              {formatBusinessDate(contact.data_contatto)}
              {contact.origine ? ` · ${contact.origine === 'social' ? 'Social' : 'Offline'}` : ''}
            </Row>
          )}

          {reminderDates.length > 0 && (
            <Row icon={CalendarClock} label="Promemoria">
              <ul className="space-y-0.5">
                {reminderDates.map((d) => (
                  <li key={d} className={d <= today ? 'text-orange-700 font-medium' : ''}>
                    {formatBusinessDate(d)}{d <= today ? ' · da ricontattare' : ''}
                  </li>
                ))}
              </ul>
            </Row>
          )}

          {contact.note && (
            <Row icon={StickyNote} label="Note">
              <p className="whitespace-pre-wrap">{contact.note}</p>
            </Row>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDetailDialog;
